import {
  Users,
  Brain,
  BarChart,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  PieChart,
  RefreshCw,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shadcnComponents/ui/avatar";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import {
  IBrandSentiments,
  ICustomerQuery,
  IEngagedAudienceAnalysisProps,
  IReachAudienceAnalysisProps,
  SelectedInfluencer,
} from "../CampaignInterface/LiveCampaignInterface";
import { OpenInNewWindow } from "@/Helpers/helper";
import { useBrand } from "@/Providers/Brand";
import { ReachAudienceAnalysis } from "../campaignReports/reach-audience-analysis";
import { EngagedAudienceAnalysis } from "../campaignReports/engaged-audience-analysis";

import { InfluencerRating } from "../Components/LiveCampaignComponents/influencer-rating";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcnComponents/ui/tabs";
import { BrandSentiment } from "../campaignReports/brand-sentiment";
import { CompetitorsValues } from "../campaignReports/competitors-values";
import { CustomerQueryAnalysis } from "../campaignReports/customer-query-analysis";

export const LiveCampaignReport = ({
  selectedInfluencers,
  collabId,
  getCollabData,
}: {
  selectedInfluencers: SelectedInfluencer[];
  collabId: string;
  getCollabData: () => void;
}) => {
  const brandState = useBrand();
  const [reportGenerated, setReportGenerated] = useState(false);
  const [activeTab, setActiveTab] = useState("reach");

  const [ratingIndex, setRatingIndex] = useState(0);

  const [activeAITab, setActiveAITab] = useState("brand-sentiment");

  const [selectedInfluencer, setSelectedInfluencer] = useState(
    selectedInfluencers?.filter((item) => item?.trackingLink)?.[0]
      ?.influencerDetails?._id
  );
  const [reachAudience, setReachAudience] =
    useState<IReachAudienceAnalysisProps>(null);
  const [engagedAudience, setEngagedAudience] =
    useState<IEngagedAudienceAnalysisProps>(null);
  const [aiBrandSentiment, setAiBrandSentiment] =
    useState<IBrandSentiments>(null);
  const [aiCustomerQuery, setAiCustomerQuery] = useState<ICustomerQuery>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [canRefresh, setCanRefresh] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const getPostAnalysis = useCallback(
    async (influencerId, isRefresh = false) => {
      try {
        setIsLoading(true);
        const result = await brandState?.getCollabPostAnalysis(
          collabId,
          influencerId,
          isRefresh
        );
        if (result?.success) {
          setReachAudience(result.reachAudience);
          setEngagedAudience(result.engagedAudience);
          setAiBrandSentiment(result.aiCommentAnalysis?.brandSentiments);
          setAiCustomerQuery(result.aiCommentAnalysis?.customerQuery);
          setReportGenerated(true);
          setCanRefresh(result.canRefresh);
          if (isRefresh) {
            setActiveTab("ai");
            setActiveAITab("brand-sentiment");
          }
        } else {
          console.log("Failed to fetch post analysis");
        }
      } catch (error) {
        console.error("Error fetching post analysis:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [brandState, collabId]
  );

  const refreshReport = async () => {
    try {
      setIsRefreshing(true);
      console.log("Refreshing report for influencer:", selectedInfluencer);
      await getPostAnalysis(selectedInfluencer, true);
    } catch (error) {
      console.error("Error refreshing report:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (selectedInfluencer) {
      console.log("Fetching post analysis for influencer:", selectedInfluencer);
      getPostAnalysis(selectedInfluencer);
    }
  }, [selectedInfluencer]);

  const profiles = selectedInfluencers
    ?.filter((item) => item?.trackingLink)
    ?.map((influencer) => influencer);

  // Add navigation functions
  const goToPreviousRating = () => {
    if (ratingIndex > 0) {
      setRatingIndex(ratingIndex - 1);
    }
  };

  const goToNextRating = (withAnimation = false, time = 2000) => {
    if (ratingIndex < profiles.length - 1) {
      if (withAnimation) {
        const currentCard = document.querySelector(
          `#rating_card_${ratingIndex}`
        );

        setTimeout(() => {
          if (currentCard) {
            currentCard.classList.add("animate-slide-out");
            setTimeout(() => {
              const nextIndex = ratingIndex + 1;

              setRatingIndex(nextIndex);

              setTimeout(() => {
                const newCard = document.querySelector(
                  `#rating_card_${nextIndex}`
                );

                if (newCard) {
                  newCard.classList.add("animate-slide-in");

                  setTimeout(() => {
                    if (newCard) newCard.classList.remove("animate-slide-in");
                    if (currentCard)
                      currentCard.classList.remove("animate-slide-out");
                  }, 500);
                }
              }, 50);
            }, 300);
          }
        }, time);
      } else {
        setRatingIndex(ratingIndex + 1);
      }
    }
  };

  const isAiDataAvailable = Boolean(
    aiBrandSentiment?.total_comments || aiCustomerQuery?.total_comments
  );

  return (
    <div className="w-full">
      <div className="border-b">
        <div className="px-6 py-4">
          <div className="flex items-center space-x-4 mb-6 p-4 bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-200 rounded-lg shadow-sm">
            {selectedInfluencers
              ?.filter((item) => item?.trackingLink)
              ?.map((influencer) => (
                <div
                  key={influencer?._id}
                  className={cn(
                    "flex flex-col items-center space-y-1 cursor-pointer border rounded-md p-2",
                    "hover:bg-amber-50 transition-colors",
                    influencer?.influencerDetails?._id === selectedInfluencer
                      ? "border-amber-500 bg-white shadow-md"
                      : "border-amber-200 bg-amber-50",
                    isLoading && "opacity-50 pointer-events-none"
                  )}
                  onClick={() =>
                    setSelectedInfluencer(influencer?.influencerDetails?._id)
                  }
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage
                      src={influencer?.influencerDetails?.profile}
                      alt={influencer?.influencerDetails?.name}
                    />
                    <AvatarFallback>
                      {influencer?.influencerDetails?.name?.slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-gray-600">
                    {influencer?.influencerDetails?.name}
                  </span>
                </div>
              ))}
          </div>
          <div className="flex justify-between items-center">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab("reach")}
                className={cn(
                  "flex items-center space-x-2 px-1 py-2 text-gray-600 border-b-2 border-transparent",
                  activeTab === "reach" && "border-red-500 text-gray-900",
                  isLoading && "opacity-50 pointer-events-none"
                )}
                disabled={isLoading}
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 3v18h18" />
                  <path d="m19 9-5 5-4-4-3 3" />
                </svg>
                <span>Reached Audience</span>
              </button>
              <button
                onClick={() => setActiveTab("engaged")}
                className={cn(
                  "flex items-center space-x-2 px-1 py-2 text-gray-600 border-b-2 border-transparent",
                  activeTab === "engaged" && "border-red-500 text-gray-900",
                  isLoading && "opacity-50 pointer-events-none"
                )}
                disabled={isLoading}
              >
                <Users className="w-4 h-4" />
                <span>Engaged Audience</span>
              </button>
              <button
                onClick={() => {
                  if (!isAiDataAvailable) return;
                  setActiveTab("ai");
                  setActiveAITab("brand-sentiment");
                }}
                className={cn(
                  "flex items-center space-x-2 px-1 py-2 text-gray-600 border-b-2 border-transparent",
                  activeTab === "ai" && "border-red-500 text-gray-900",
                  !isAiDataAvailable && "opacity-60 cursor-not-allowed",
                  isLoading && "opacity-50 pointer-events-none"
                )}
                disabled={!isAiDataAvailable || isLoading}
              >
                <Brain className="w-4 h-4" />
                <span>
                  AI Comment Analysis{!isAiDataAvailable && " [Coming Soon]"}
                </span>
              </button>
            </div>

            {reportGenerated && canRefresh && (
              <button
                onClick={refreshReport}
                disabled={isRefreshing || isLoading}
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors",
                  (isRefreshing || isLoading) &&
                    "opacity-50 cursor-not-allowed bg-red-400"
                )}
              >
                <RefreshCw
                  className={cn("w-4 h-4", isRefreshing && "animate-spin")}
                />
                <span>
                  {isRefreshing
                    ? "Refreshing..."
                    : !isAiDataAvailable && canRefresh
                    ? "Generate Comment Analysis"
                    : "Refresh Comment Analysis"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {reportGenerated ? (
        <div className="p-6 grid grid-cols-12 gap-6">
          <div className="col-span-9">
            {isLoading || isRefreshing ? (
              <div className="flex flex-col items-center justify-center w-full h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mb-4"></div>
                <p className="text-gray-600">
                  {isRefreshing ? "Refreshing data..." : "Loading data..."}
                </p>
              </div>
            ) : (
              <>
                {activeTab === "reach" &&
                  (reachAudience?.summary?.role ||
                  reachAudience?.summary?.location ||
                  reachAudience?.summary?.seniority ? (
                    <ReachAudienceAnalysis
                      metrics={reachAudience?.metrics}
                      summary={reachAudience?.summary}
                      distribution={reachAudience?.distribution}
                    />
                  ) : (
                    <ReportNotGenerated collabId={collabId} />
                  ))}
                {activeTab === "engaged" &&
                  (engagedAudience?.overall?.topIndustry ||
                  engagedAudience?.overall?.topLocation ||
                  engagedAudience?.overall?.topRole ||
                  engagedAudience?.overall?.topSeniority ? (
                    <EngagedAudienceAnalysis
                      metrics={reachAudience?.metrics}
                      likeDistribution={engagedAudience?.likeDistribution}
                      commentDistribution={engagedAudience?.commentDistribution}
                      overall={engagedAudience?.overall}
                    />
                  ) : (
                    <ReportNotGenerated collabId={collabId} />
                  ))}
                {activeTab === "ai" && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex-1">
                        <Tabs
                          value={activeAITab}
                          onValueChange={setActiveAITab}
                          className="w-full"
                        >
                          <div className="flex justify-between items-center">
                            <TabsList className="bg-[#F5F5F5] p-1">
                              <TabsTrigger
                                value="brand-sentiment"
                                className={cn(
                                  "flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#0A66C2]",
                                  !aiBrandSentiment &&
                                    "opacity-60 cursor-not-allowed"
                                )}
                                disabled={!aiBrandSentiment}
                              >
                                <PieChart className="h-4 w-4" />
                                Brand Sentiment
                                {!aiBrandSentiment && " [Coming Soon]"}
                              </TabsTrigger>
                              <TabsTrigger
                                value="customer-query"
                                className={cn(
                                  "flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#0A66C2]",
                                  !aiCustomerQuery &&
                                    "opacity-60 cursor-not-allowed"
                                )}
                                disabled={!aiCustomerQuery}
                              >
                                <MessageSquare className="h-4 w-4" />
                                Customer Query Analysis
                                {!aiCustomerQuery && " [Coming Soon]"}
                              </TabsTrigger>
                              {/* <TabsTrigger
                                value="competitors-values"
                                className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-[#0A66C2]"
                              >
                                <BarChart className="h-4 w-4" />
                                Competitors Mentioned
                              </TabsTrigger> */}
                            </TabsList>
                          </div>

                          <TabsContent value="brand-sentiment">
                            {aiBrandSentiment?.total_comments ? (
                              <BrandSentiment
                                brandSentimentData={aiBrandSentiment}
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
                                <PieChart className="h-10 w-10 text-gray-400 mb-2" />
                                <p className="text-gray-600 font-medium">
                                  Brand Sentiment Analysis Not Available
                                </p>
                                <p className="text-sm text-gray-500 mt-2">
                                  This feature is still in development.
                                </p>
                              </div>
                            )}
                          </TabsContent>

                          <TabsContent value="customer-query">
                            {aiCustomerQuery?.total_comments ? (
                              <CustomerQueryAnalysis
                                customerQueryData={aiCustomerQuery}
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
                                <MessageSquare className="h-10 w-10 text-gray-400 mb-2" />
                                <p className="text-gray-600 font-medium">
                                  Customer Query Analysis Not Available
                                </p>
                                <p className="text-sm text-gray-500 mt-2">
                                  This feature is still in development.
                                </p>
                              </div>
                            )}
                          </TabsContent>

                          <TabsContent value="competitors-values">
                            <CompetitorsValues
                              influencer={selectedInfluencer}
                            />
                          </TabsContent>
                        </Tabs>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="col-span-3">
            {/* Rating card section with sticky behavior and highlighted styling */}
            <div className="sticky top-6">
              <div className="bg-white p-4 rounded-lg border border-red-600 shadow-sm">
                <div className="space-y-2 mb-4">
                  <div className="flex items-center">
                    <h3 className="text-lg font-bold text-red-600">
                      Rate the Influencer
                    </h3>
                  </div>
                  <p className="text-xs font-medium text-gray-600">
                    Your feedback stays private and shapes the influencer's
                    platform ranking
                  </p>
                </div>

                {/* Rating card with animation container */}
                <div className="overflow-hidden">
                  {" "}
                  {/* Container to clip animations */}
                  <div
                    id={`rating_card_${ratingIndex}`}
                    className="bg-white rounded-lg border border-[#C7E6FF] shadow-sm transition-all duration-300"
                  >
                    <InfluencerRating
                      name={profiles[ratingIndex]?.influencerDetails?.name}
                      image={profiles[ratingIndex]?.influencerDetails?.profile}
                      initials={profiles[
                        ratingIndex
                      ]?.influencerDetails?.name?.slice(0, 2)}
                      collabId={collabId}
                      influencerId={
                        profiles[ratingIndex]?.influencerDetails?._id
                      }
                      isAlreadySubmitted={
                        profiles[ratingIndex]?.Rating?.status === 1
                      }
                      onSubmit={() => {
                        getCollabData();
                        // Only animate to next card if there is a next card
                        if (ratingIndex < profiles.length - 1) {
                          setTimeout(() => goToNextRating(true), 800);
                        }
                      }}
                      rating={profiles[ratingIndex]?.Rating?.rating}
                    />
                  </div>
                </div>

                {/* Navigation arrows below the card */}
                <div className="flex justify-center items-center gap-4 mt-4">
                  {ratingIndex > 0 && (
                    <button
                      onClick={goToPreviousRating}
                      className="h-10 w-10 rounded-full bg-white flex items-center justify-center border border-red-200 hover:bg-red-50 transition-colors"
                      aria-label="Previous creator"
                    >
                      <ChevronLeft className="h-5 w-5 text-red-500" />
                    </button>
                  )}
                  <div className="text-sm text-red-500 font-medium">
                    {ratingIndex + 1} of {profiles.length}
                  </div>
                  {ratingIndex < profiles.length - 1 && (
                    <button
                      onClick={() => goToNextRating(true, 300)}
                      className="h-10 w-10 rounded-full bg-white flex items-center justify-center border border-red-200 hover:bg-red-50 transition-colors"
                      aria-label="Next creator"
                    >
                      <ChevronRight className="h-5 w-5 text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6">
          {isLoading || isRefreshing ? (
            <div className="flex flex-col items-center justify-center w-full h-[50vh]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mb-4"></div>
              <p className="text-gray-600">
                {isRefreshing ? "Refreshing data..." : "Loading data..."}
              </p>
            </div>
          ) : (
            <ReportNotGenerated collabId={collabId} />
          )}
        </div>
      )}
    </div>
  );
};

export const ReportNotGenerated = (collabId) => {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-center">
      <div className="bg-gray-50 rounded-lg p-8 max-w-2xl shadow-sm border border-gray-200">
        <div className="mb-6">
          <div className="bg-red-100 rounded-full p-3 inline-flex items-center justify-center mb-4">
            <BarChart className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {collabId === "sample"
              ? "Report Generated"
              : " Report Not Generated Yet"}
          </h2>
          <p className="text-gray-600 mb-6">
            {collabId !== "sample" &&
              "The report will be generated after the campaign ends. Until then, you can explore a sample report analysis to see what insights you'll receive."}
          </p>
          <button
            onClick={() =>
              collabId === "sample"
                ? OpenInNewWindow({
                    link: "https://report.anchors.in/zepto",
                  })
                : OpenInNewWindow({
                    link: "https://x.anchors.in/56ODZY",
                  })
            }
            className="inline-flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
          >
            <BarChart className="h-5 w-5 mr-2" />
            {collabId === "sample" ? "Go to Report" : "Go to Sample Campaign"}
          </button>
        </div>

        <div className="text-sm text-gray-500 mt-4 pt-4 border-t border-gray-200">
          <p>
            Sample reports are based on real data but aren't actual
            collaborations. For detailed analysis of your brand's influencer
            marketing campaigns, please wait until the campaign concludes.
          </p>
        </div>
      </div>
    </div>
  );
};
